import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage, Form, Formik } from 'formik';
import React, {FC, useEffect, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import ActivityFrame from '../components/activity-frame/activity-frame';
import Input from '../components/input/input';
import { useLocale } from '../contexts/localeContext';
import { TranslationKeys } from '../contexts/TranslationKeys';
import passwordIcon from '../images/password_icon.svg';
import brandLogo from '../images/brand_logo_color.png';
import productLogo from '../images/product_logo_menu.svg';
import usernameIcon from '../images/user_name_icon.svg';
import msSignOn from '../images/ms_symbol_SignIn.svg';
import googleSignOn from '../images/google_symbol_signIn.svg';
import { authLogin, changeUserPassword, getLoginById, storeSecurityPolicy } from '../services/loginService';
import { IAppState } from '../store/types';
import { setUserInfoState } from '../store/user-info/actions';
import { getErrorMessage } from "../services/utilities/ServiceError";
import { useDialog } from "../components/dialog/DialogContext";
import MfaModal from "../components/login/mfa/MfaModal";
import { DialogButton, DialogContent } from "../components/dialog/Dialog";
import { ButtonTypes } from "../models/ButtonTypes";
import { requiredValidation, validatePassword } from "../utilities/Validation";
import footerLogoIcon from '../images/CompanyLogo.png';
import { useGlobalState } from "../Providers";
import { toast } from "react-toastify";
import { LOGIN_STATUS } from "../constants/user-login-constants";
import ChangeUserPasswordModal from "@/components/change-password/change-user-password-modal.tsx";
import {AUTH_TYPE} from "@/constants/account-constants.ts";
import {getTranslationMessageForPasswordExpiring} from "@/services/utilities/loginsUtils.ts";

const initialValues = {
    remember: false,
    username: '',
    password: '',
    authCode: undefined,
    status: LOGIN_STATUS.OK
};

type Props = {
    version: String,
    cloudImproHosted: boolean,
    identityProviderId: number
}

const LoginForm :FC<Props> = ({ version, cloudImproHosted, identityProviderId}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { translate } = useLocale();
    const isAuthenticated = useSelector((state: IAppState) => (state.userInfo.isAuthenticated));
    const [mfaValues, setMfaValues] = useState(initialValues); // Store in case we need to resubmit for MFA
    const [busy, setBusy] = useState(false);
    const [showPassword, setShowPassword] = useState(false)
    const [isOpenChangePwd, setIsOpenChangePwd] = useState(false);
    const [isOpenMfa, setIsOpenMfa] = useState(false);
    const enableLogin = useState(true);
    const [qrCodeImage, setQrCodeImage] = useState("");
    const dialog = useDialog();
    const userLogin = useSelector((state: IAppState) => (state.userInfo));
    let messageDetails;
    const { loadBuildInfo } = useGlobalState() ?? {};
    const redirectUrl = 'api/auth/login';

    useEffect(() => {
        if (isAuthenticated) {
            history.push('/')
        }
    }, [isAuthenticated])

    const onChangePassword = (passwordValues) => {
        changeUserPassword(userLogin.id, passwordValues.newPassword, passwordValues.currentPassword)
            .then(() => {
                toast.success(translate.byKey(TranslationKeys.passwordChangedSuccessfully));
                updateIsAuthenticated(userLogin);
                setIsOpenChangePwd(false)
            }).catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
            }).finally(() => {
                setIsOpenChangePwd(false);
            })
    }

    const updateIsAuthenticated = (userLogin: any) => {
        dispatch(
            setUserInfoState({
                ...userLogin,
                isAuthenticated: true
            })
        );
    }

    const getExpiringButtonOptions = (userLogin: any): DialogButton[] => {
        if (messageDetails.passwordExpired || messageDetails.failsRequirementCheck) {
            return [
                {
                    id: "changePasswordId",
                    text: translate.byKey(TranslationKeys.changePassword),
                    onClick: () => setIsOpenChangePwd(true),
                    type: ButtonTypes.RedRoundedButton
                }
            ]
        }

        return [
            {
                id: "doNotChangePasswordId",
                text: translate.byKey(TranslationKeys.ok),
                onClick: () => updateIsAuthenticated(userLogin),
                type: ButtonTypes.GreenRoundedButton
            },
            {
                id: "changePasswordId",
                text: translate.byKey(TranslationKeys.changePassword),
                onClick: () => setIsOpenChangePwd(true),
                type: ButtonTypes.GreenRoundedButton
            }];
    }

    // handleSuccessfulLogin
    const handleLogin = async (fields, userDetails) => {
        try {
            // Clear the MFA settings
            setMfaValues(initialValues);
            sessionStorage.setItem("token", JSON.stringify(userDetails.accessToken));
            const userLogin = await getLoginById(userDetails.userLoginId) as any;
            loadBuildInfo && loadBuildInfo();

            dispatch(
                setUserInfoState({
                    ...userLogin,
                    remember: fields.remember ?? false,
                    isAuthenticated: false
                })
            );

            messageDetails = await storeSecurityPolicy(userLogin.id, userLogin.securityPolicyId, fields) as any;
            if (!messageDetails.passwordIsGood) {
                if (messageDetails.firstLogin || messageDetails.isUsingDefaultPassword) {
                    // we force a new password
                    const dialogButton: DialogButton[] = [{
                        id: "changePasswordId",
                        text:translate.byKey(TranslationKeys.changePassword),
                        onClick: () => setIsOpenChangePwd(true),
                        type: ButtonTypes.RedRoundedButton
                    }]

                    const dialogContent: DialogContent = {
                        title: messageDetails.isUsingDefaultPassword ? translate.byKey(TranslationKeys.passwordUpdateRequired) : translate.byKey(TranslationKeys.securityPolicyPassword),
                        message:  messageDetails.isUsingDefaultPassword? translate.byKey(TranslationKeys.changeDefaultPassword) : translate.byKey(TranslationKeys.firstLoginPasswordChange),
                        buttons: dialogButton
                    }
                    dialog.pushInfo(dialogContent);
                }
                else {
                    // Give a warning about password expiring and option to change the password.
                    const dialogContent: DialogContent = {
                        title: translate.byKey(TranslationKeys.securityPolicyPassword),
                        message: getTranslationMessageForPasswordExpiring(messageDetails),
                        buttons: getExpiringButtonOptions(userLogin)
                    }
                    dialog.pushInfo(dialogContent);
                }
            }
            else {
                // No reason to stop the user from login.
                updateIsAuthenticated(userLogin);
            }
        } catch (error) {
            console.error("Error storing security policy: ", error);
        }
    }

    const submit = async (fields) => {
        setBusy(true);
        try {
            const userDetails = await authLogin(fields, {}) as any
            const status = userDetails.status;
            const qrCodeImage = userDetails.qrCode;

            // Register MFA and capture validation code
            if (status == LOGIN_STATUS.MFA_REGISTRATION) {
                setMfaValues(prev => ({
                    ...prev,
                    remember: fields.remember,
                    username: fields.username,
                    password: fields.password,
                    status: status
                }));
                setQrCodeImage(qrCodeImage);
                setIsOpenMfa(true);
            }
            // Capture MFA validation code
            else if (status == LOGIN_STATUS.MFA_CODE) {
                setMfaValues(prev => ({
                    ...prev,
                    remember: fields.remember,
                    username: fields.username,
                    password: fields.password,
                    status: status
                }));
                setIsOpenMfa(true);
            }
            // Handle Success / Failure
            else {
                handleLogin(fields, userDetails);
            }
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
        } finally {
            setBusy(false);
        }
    }

    const googleLoginSubmit = async () => {
        if (identityProviderId === AUTH_TYPE.GOOGLE) {
            ssoLoginSubmit();
            return;
        }
        dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.googleSignInNotActive)})
    }

    const microsoftLoginSubmit = async () => {
        if (identityProviderId === AUTH_TYPE.ENTRA) {
            ssoLoginSubmit();
            return;
        }
        dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.msSignInNotActive)})
    }

    const ssoLoginSubmit = async () => {
        setBusy(true);
        try {
            // Post requests can cause issues if they are not ended correctly, found it easier to rather call this endpoint
            // and let it handle the redirection to the identity provider and back to the app, than to try to handle the redirection in the frontend.
            window.location.href = redirectUrl;
            return;
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
        } finally {
            setBusy(false);
        }
    }

    const onSubmitMfa = (fields) => {
        setIsOpenMfa(false); // Close the modal
        submit(fields);
    }

    return (
        <div className='flex flex-col items-center p-2 justify-center'>
            <img src={brandLogo} style={{width:'19rem'}} alt="" />
            <img src={productLogo} className="w-64 product-logo pl-4 pr-6 pt-6" alt="" />
            <Formik
                initialValues={initialValues}
                onSubmit={submit}>
                {() => (
                    <ActivityFrame busy={busy}>
                        <Form style={{ minWidth: '300px' }}>
                            <span className="text-center my-1 block">{translate.byKey(TranslationKeys.signInWithExistingAccount)}</span>
                            <div className="mb-4">
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <img className="h-5 pr-2 text-portal-blue-primary" src={usernameIcon}/>
                                    </div>
                                    <div id='userNameInputField'>
                                        <Input
                                            type="text"
                                            name="username"
                                            placeholder={translate.byKey(TranslationKeys.username)}
                                            validation={(value) => requiredValidation(value, translate)}
                                            className="appearance-none pl-10 block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"/>
                                    </div>
                                </div>
                                <ErrorMessage name="username">
                                    {msg => <div id='userNameLabel' className="text-red-700 text-xs">{msg}</div>}
                                </ErrorMessage>
                            </div>
                            <div className="mt-1 relative rounded-md shadow-sm" hidden={!enableLogin}>
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <img className="h-5 pr-2 text-portal-blue-primary" src={passwordIcon} />
                                </div>
                                <div id='passwordInputField'>
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        placeholder={translate.byKey(TranslationKeys.password)}
                                        validation={(value) => requiredValidation(value, translate)}
                                        className="appearance-none pl-10 block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center ">
                                            <FontAwesomeIcon
                                                className="text-gray-600 cursor-pointer"
                                                onClick={() => setShowPassword(!showPassword)}
                                                icon={showPassword ? faEye : faEyeSlash}/>
                                        </div>
                                    </Input>
                                </div>
                            </div>
                            <ErrorMessage name="password">
                                {msg => <div id='passwordLabel' className="text-red-700 text-xs">{msg}</div>}
                            </ErrorMessage>
                            {/* <div className="flex items-center justify-between mt-3 mb-6">
                                <div className="flex items-center">
                                    <input
                                        id="remember_me"
                                        name="remember_me"
                                        type="checkbox"
                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="remember_me" className="ml-2 block text-xs text-gray-900">
                                        Remember me
                                    </label>
                                </div>

                                <div className="text-xs">
                                    <a href="#" className="font-small text-indigo-600 hover:text-indigo-500">
                                        Forgot your password?
                                    </a>
                                </div>
                            </div> */}
                            <div className="mt-3 py-1">
                                <input
                                    id='loginBtn'
                                    type='submit'
                                    value={translate.byKey(TranslationKeys.login)}
                                    className='p-2 bg-portal-blue-primary text-white w-full rounded-lg font-bold text-sm cursor-pointer'>
                                </input>
                            </div>
                            {/* SSO options */}
                            {identityProviderId != AUTH_TYPE.NOT_ACTIVE && <div className="flex flex-col mt-3 py-1 justify-center items-center">
                                <span className="text-center text-m text-gray-800 mb-2">{translate.byKey(TranslationKeys.ssoSign)}</span>
                                <div className="grid grid-cols-1 gap-4">
                                    {identityProviderId == AUTH_TYPE.ENTRA && <button onClick={microsoftLoginSubmit} type='button'>
                                        <img src={msSignOn} alt={translate.byKey(TranslationKeys.msSignIn)} className="h-8"/>
                                    </button>}
                                    {identityProviderId === AUTH_TYPE.GOOGLE && <button onClick={googleLoginSubmit} type='button'>
                                        <img src={googleSignOn} alt={translate.byKey(TranslationKeys.googleSignIn)} className="h-10"/>
                                    </button>}
                                </div>
                            </div> }

                            {/* Impro-Hosted cloud does not include the version in the splash. Show the version beneath the login form.
                            Hide the version for On-Prem and Self-Hosted cloud which include the version in the splash. */}
                            {cloudImproHosted === true && (
                                <span className="flex flex-col items-center p-2 justify-center text-xs">{version}</span>
                            )}

                            {/* Company logo */}
                            <div className="footer-logo">
                                <img src={footerLogoIcon} alt="impro logo" className="mt-12 pl-12 h-auto max-w-full w-64" />
                            </div>
                        </Form>
                    </ActivityFrame>
                )}
            </Formik>
            <ChangeUserPasswordModal isOpen={isOpenChangePwd} setIsOpen={setIsOpenChangePwd} onChangePassword={onChangePassword} validateNewPassword={validatePassword}/>
            <MfaModal isOpen={isOpenMfa} setIsOpen={setIsOpenMfa} onSubmitMfa={onSubmitMfa} qrCodeImage={qrCodeImage} mfaValues={mfaValues} />
        </div>
    )
};

export default LoginForm;
